import React, { useEffect, useState, useContext } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from '../BasicElement';
import Button from '../Button';
import Image, { IImage } from '../Image';
import Loading from '../Loading/index';
import Modal from '../Modal';
import withAppContext from '../../hooks/withAppContext';
import { handleGtmTags } from '../GTMHandler';
import Icon from '../Icon';
import VHSRegistrationModal from './VHSRegistrationModal';
import {VHSFunctions} from "../VHSFunctions";

export interface ICartItem {
	itemID?: string;
	image?: IImage;
	code?: string;
	title?: string;
	amount?: number;
	amountFormatted?: string;
	studentID?: number;
	fastTrack?: number;
	prerequisite?: string;
}

export interface ICartSummary {
	title?: string;
	subTotalLabel?: string;
	subTotalAmount?: number;
	subTotalAmountFormatted?: string;
	totalLabel?: string;
	totalAmount?: number;
	totalAmountFormatted?: string;
}

export interface IStudent {
	id?: number;
	name?: string;
}

export interface ICartProps extends IBaseElementProps {
	children?: any;
	switchLabel?: string;
	cartHeading?: string;
	cartItems?: ICartItem[];
	cartSummary?: ICartSummary;
	addMoreCoursesURL?: string;
	billingDetailsURL?: string;
	studentRegistrationForm?: string;
	studentConfirmationPage?: string;
	pluginURI?: string;
	stylesheetURI?: string;
	students?: IStudent[];
	priceToolTip?: string;
	fastTrackToolTip?: string;
}

const VHSCart: React.FC<ICartProps> = withAppContext(
	({
		 context,
		 children,
		 className,
		 classNames = [],
		 switchLabel,
		 cartHeading,
		 cartItems,
		 cartSummary,
		 addMoreCoursesURL,
		 billingDetailsURL,
		 studentRegistrationForm,
		 studentConfirmationPage,
		 pluginURI,
		 stylesheetURI,
		 students,
		 priceToolTip,
		 fastTrackToolTip
	 }) => {
		// @ts-ignore
		const { Api } = useContext(context);

		const [busy, setBusy] = useState([]);
		const [cartItemForEdit, setCartItemForEdit] = useState({
			itemID: '',
		} as ICartItem);
		const [cartItemToRemove, setCartItemToRemove] = useState({
			title: '',
			code: '',
			studentID: 0,
		});
		const [errorMessage, setErrorMessage] = useState('');
		const [refreshCartItemDisplay, setRefreshCartItemDisplay] =
			useState(false);
		const [removeBusy, setRemoveBusy] = useState([]);
		const [showEditModal, setShowEditModal] = useState(false);
		const [showRemoveModal, setShowRemoveModal] = useState(false);
		const [creatingStudent, setCreatingStudent] = useState(false);
		const [selectedStudent, setSelectedStudent] = useState(0);
		const [formLoaded, setFormLoaded] = useState(false);
		const numberFormatter = Intl.NumberFormat('en-CA', {
			style: 'currency',
			currency: 'CAD',
		});

		const handleEdit = (item, index) => {
			item.index = index;
			setSelectedStudent(
				item.studentID != 0
					? item.studentID
					: students[0]?.id
						? students[0].id
						: 0,
			);
			if (
				cartItemForEdit !== null &&
				item.itemID != cartItemForEdit.itemID
			) {
				setRefreshCartItemDisplay(true);
			}

			// set the current course so we can use it within the modal
			// @ts-ignore
			window.current_course = item.itemID;
			setCartItemForEdit(item);
			setShowEditModal(true);
		};

		const handleFastTrackSwitch = (switchBox, item, index) => {
			setBusy((prev) => [...prev, item.itemID]);
			item.index = index;
			item.fastTrack = switchBox.checked ? 1 : 0;
			if (
				cartItemForEdit !== null &&
				item.itemID != cartItemForEdit.itemID
			) {
				setRefreshCartItemDisplay(true);
			}

			setCartItemForEdit(item);

			Api.updateCartItem(item)
				.then((results) => {
					handleGtmTags(results);
					refreshCartItems(item, results);
				})
				.finally(() => {
					setBusy([]);
				})
				.catch((message) => {
					setErrorMessage(message);
					setBusy([]);
				});
		};

		const handleRemove = (item) => {
			setCartItemToRemove(item);
			setShowRemoveModal(true);
		};

		const refreshCartItems = (item, results) => {
			cartItems[item.index].amountFormatted = numberFormatter.format(
				results.item_price,
			);
			cartItems[item.index].amount = results.item_price;
			cartSummary.subTotalAmount = 0;
			cartSummary.totalAmount = 0;
			cartItems.forEach((item) => {
				// @ts-ignore
				cartSummary.subTotalAmount += parseFloat(item.amount);
				// @ts-ignore
				cartSummary.totalAmount += parseFloat(item.amount);
			});
			cartSummary.subTotalAmountFormatted = numberFormatter.format(
				cartSummary.subTotalAmount,
			);
			cartSummary.totalAmountFormatted = numberFormatter.format(
				cartSummary.totalAmount,
			);
		};

		const assignStudent = (item) => {
			setBusy((prev) => [...prev, item.itemID]);
			item.studentID = selectedStudent;
			// save the assignment of student to the cart
			Api.updateCartItem(item)
				.then((results) => {
					handleGtmTags(results);
					refreshCartItems(item, results);
				})
				.finally(() => {
					setBusy([]);
				})
				.catch((message) => {
					setErrorMessage(message);
					setBusy([]);
				});
			setShowEditModal(false);
		};

		const editStudent = () => {
			window.location.href = `${studentConfirmationPage}?studentID=${selectedStudent}`;
		};

		const allowCancel = () => {
			setShowEditModal(false);
			setCreatingStudent(false);
			setBusy([]);
		};

		const processRemove = (item) => {
			setShowRemoveModal(false);
			setRemoveBusy((prev) => [...prev, item.itemID]);
			Api.removeCourseFromCart(item)
				.then((results) => {
					// refresh cart page
					handleGtmTags(results);
					window.location.href = results.url;
				})
				.catch((message) => {
					setErrorMessage(message);
					setRemoveBusy([]);
				});
		};

		const createStudent = () => {
			setCreatingStudent(true);
			setTimeout(() => {
				setFormLoaded(true);
			}, 500);
		};

		const getStudentName = (studentID) => {
			let student_name = '';
			students.forEach((student) => {
				if (student.id == studentID) {
					student_name = student.name;
				}
			});
			return student_name;
		};

		const checkUnassignedStudents = () => {
			let proceedToCheckout = true;
			cartItems.forEach((item, index) => {
				if (item.studentID == 0 && proceedToCheckout) {
					handleEdit(item, index);
					proceedToCheckout = false;
				}
			});
			if (proceedToCheckout) {
				window.location.href = billingDetailsURL;
			}
		};

		useEffect(() => {
			if (cartItemForEdit !== null) {
				setRefreshCartItemDisplay(false);
			}
		}, [cartItemForEdit]);


		useEffect(() => {
			setSelectedStudent(students.length > 0 ? students[0].id : 0);
			VHSFunctions.initTooltips();
		}, []);

		return (
			<fieldset
				disabled={busy.length > 0 || removeBusy.length > 0}
			>
				<h1>Cart</h1>
				<div className={className || cn('vhs-cart', ...classNames)}>
					{errorMessage && (
						<div className="error-message">{errorMessage}</div>
					)}
					<div className="row">
						<div className="vhs-cart__left col-12 col-lg-8">
							<div className="vhs-cart__items">
								{(cartItems || []).map((item, index) => (
									<div className="vhs-cart__item" key={index}>
										<div className="vhs-cart__item-left">
											<div className="vhs-cart__image-wrapper">
												<div className="vhs-cart__image-position">
													{item.image && (
														<Image
															className="vhs-cart__image"
															{...item.image}
														/>
													)}
												</div>
											</div>
											<div className="vhs-cart__item-left-content">
												{item.code && (
													<div className="vhs-cart__code">
														{item.code}
													</div>
												)}
												{item.title && (
													<div className="vhs-cart__title">
														{item.title}
													</div>
												)}
												{item.prerequisite && (
													<div
														className="vhs-cart__prerequisite"
														dangerouslySetInnerHTML={{
															__html: item.prerequisite,
														}}
													/>
												)}
												{removeBusy.indexOf(
													item.itemID,
												) !== -1 && <Loading inline />}
											</div>
										</div>
										<div className="vhs-cart__item-right">
											<div className="vhs-cart__item-right-content">
												{item.amountFormatted && (
													<div className="vhs-cart__amount">
														{item.amountFormatted}
														<button
															type="button"
															className="vhs-cart__tooltip"
															data-bs-toggle="tooltip"
															data-bs-html="true"
															data-bs-placement="top"
															data-bs-trigger="click"
															data-bs-title={
																priceToolTip
															}
														>
															<Icon name="info" />
														</button>
													</div>
												)}
												<div className="vhs-cart__student">
													{' '}
													{item.studentID == 0 ? (
														<>
															<strong>Student:</strong>{' '}
															<br />
															<a
																href="#"
																onClick={() =>
																	handleEdit(
																		item,
																		index,
																	)
																}
															>
																Select a Student
															</a>
														</>
													) : (
														<strong>Student:</strong>
													)}{' '}
													<br />{' '}
													{getStudentName(
														item.studentID,
													)}
												</div>
												{switchLabel && (
													<label className="vhs-signup__switch">
														<input
															type="checkbox"
															onClick={(e) =>
																handleFastTrackSwitch(
																	e.currentTarget,
																	item,
																	index,
																)
															}
															checked={
																item.fastTrack ==
																1
															}
														/>
														<span className="vhs-signup__switch-slider" />
														<span className="vhs-signup__switch-label">
															{switchLabel}
														</span>
														<button
															type="button"
															className="vhs-cart__tooltip"
															data-bs-toggle="tooltip"
															data-bs-html="true"
															data-bs-placement="top"
															data-bs-trigger="click"
															data-bs-title={
																fastTrackToolTip
															}
														>
															<Icon name="info" />
														</button>
													</label>
												)}
												{busy.indexOf(item.itemID) !==
												-1 && <Loading inline />}
												<Button
													data-bs-toggle="modal"
													href="#"
													data-bs-target="#vhs-cart-add"
													className="vhs-cart__edit"
													onClick={() =>
														handleEdit(item, index)
													}
												>
													Edit Student
												</Button>

												{removeBusy.indexOf(
													item.itemID,
												) == -1 && (
													<Button
														data-bs-toggle="modal"
														data-bs-target="#vhs-cart-remove"
														className="vhs-cart__delete"
														onClick={() =>
															handleRemove(item)
														}
													>
														Remove Course
													</Button>
												)}
											</div>
										</div>
									</div>
								))}
							</div>
						</div>
						<div className="vhs-cart__right col-12 col-lg-4">
							<div className="vhs-cart__sticky">
								<h3>{cartSummary.title}</h3>
								<dl className="vhs-cart__summary">
									<dt className="vhs-cart__summary-label">
										{cartSummary.subTotalLabel}
									</dt>
									<dd className="vhs-cart__summary-amount">
										{cartSummary.subTotalAmountFormatted}
									</dd>
									<dt className="vhs-cart__summary-label">
										{cartSummary.totalLabel}
									</dt>
									<dd className="vhs-cart__summary-amount">
										{cartSummary.totalAmountFormatted}
									</dd>
								</dl>
								<div className="vhs-cart__buttons">
									<a
										href={addMoreCoursesURL}
										className="btn btn-sm btn-primary"
									>
										{' '}
										Add More Courses
									</a>
									<a
										href="#"
										className="btn btn-sm btn-secondary"
										onClick={checkUnassignedStudents}
									>
										Checkout{' '}
									</a>
								</div>
							</div>
						</div>
					</div>
					{showEditModal &&
					!refreshCartItemDisplay &&
					cartItemForEdit && (
						<Modal
							id="vhs-cart-add"
							className="vhs-cart__modal"
							modalClassNames={['modal-dialog-centered']}
							headerClassNames={[]}
							bodyClassNames={[]}
							closeOnOutsideClick={false}
							onCancel={() => allowCancel()}
							trapFocus={true}
						>
							{!creatingStudent && (
								<>
									<h1>Who is This Course for?</h1>
									{cartItemForEdit.title && (
										<h2>
											{cartItemForEdit.title} (
											{cartItemForEdit.code})
										</h2>
									)}
									<div
										className={cn(
											'modal-items',
											students.length == 0 &&
											'single',
										)}
									>
										{students.length > 0 && (
											<div className="modal-item">
												<h3>Existing Student</h3>
												<div className="modal-select">
													<select
														id="student-select"
														value={
															selectedStudent
														}
														// @ts-ignore
														onChange={(e) =>
															setSelectedStudent(
																parseInt(e.target.value),
															)
														}
													>
														{students.map(
															(
																student,
																index,
															) => (
																<option
																	key={
																		index
																	}
																	value={
																		student.id
																	}
																>
																	{
																		student.name
																	}
																</option>
															),
														)}
													</select>
													<label />
												</div>
												<div className="modal-buttons">
													<Button
														onClick={() =>
															assignStudent(
																cartItemForEdit,
															)
														}
													>
														Go
													</Button>
													<Button
														onClick={() =>
															editStudent()
														}
													>
														Edit
													</Button>
												</div>
											</div>
										)}
										<div className="modal-item">
											<h3>New Students</h3>
											<Button
												classNames={[
													'vhs-cart__add-student',
												]}
												onClick={() =>
													createStudent()
												}
											>
												Add a New Student
											</Button>
										</div>
									</div>
								</>
							)}
							{creatingStudent && (
								<VHSRegistrationModal
									formLoaded={formLoaded}
									studentRegistrationForm={studentRegistrationForm}
									setCreatingStudent={(value) => setCreatingStudent(value)}
									setFormLoaded={(value) => setFormLoaded(value)}
									creatingStudent={creatingStudent}
									pluginURI={pluginURI}
									stylesheetURI={stylesheetURI}
								/>
							)}
						</Modal>
					)}
					{showRemoveModal && cartItemToRemove && (
						<Modal
							id="vhs-cart-remove"
							className="vhs-cart__modal"
							modalClassNames={['modal-dialog-centered']}
							headerClassNames={[]}
							bodyClassNames={[]}
							onCancel={() => setShowRemoveModal(false)}
						>
							<h3>
								Are you sure you want to remove "
								{cartItemToRemove?.title} (
								{cartItemToRemove?.code}
								)" assigned to Student{' '}
								{getStudentName(
									cartItemToRemove?.studentID,
								)}{' '}
								from the cart?
							</h3>
							<div className="vhs-cart__buttons">
								<Button
									className="btn btn-primary btn-md"
									onClick={() =>
										processRemove(cartItemToRemove)
									}
								>
									Yes
								</Button>
								<Button
									className="btn btn-secondary btn-md"
									aria-label="Close"
									onClick={() => setShowRemoveModal(false)}
								>
									No
								</Button>
							</div>
						</Modal>
					)}
				</div>
			</fieldset>
		);
	},
);

export default VHSCart;
